import { useConciergePermissions } from '@/hooks/use-concierge-permissions';
import { useLatestConversationId } from '@/hooks/use-latest-conversation-id';
import { useWorkflow } from '@/hooks/use-workflow-context';
import { useAllWorkflowInstances } from '@/hooks/use-workflows';
import { AmplitudeFeatureFlagNames } from '@/libs/feature-flags';
import { logger } from '@/libs/logger';
import {
    ConciergeSource,
    conciergeSourceQueryString,
    guidePageSourceQueryString,
    isNewPlanQueryString,
} from '@/utils/constants';
import { routes } from '@/utils/routes';
import { CheckinTypeKey } from '@/utils/usercrypt/checkins';
import {
    FtueStepDefinitionIds,
    STORED_RETAKING_SURVEY_KEY,
    FtueStoredStepName,
    FtueStoredCheckinTypeName,
    clearFtueSessionStorage,
} from '@/utils/workflows';
import { EnumGuidePageSource } from '@calm/evexia-analytics';
import {
    ConversationResponse,
    ConversationRequest,
    UsercryptCheckinEntrySummary,
    ConversationConversationTypeEnum,
    WorkflowWorkflowState,
} from '@calm/evexia-web-sdk';
import isSSR from '@calm/web-common/dist/isSSR';
import { ConversationInclude } from '@rhg/hooks/use-ai-conversations';
import { useAmplitudeExperiment } from '@rhg/hooks/use-experiments';
import { useSaveAiConversationData } from '@rhg/hooks/use-save-ai-conversation';
import { useRouter } from 'next/router';
import {
    ReactNode,
    createContext,
    useState,
    useMemo,
    useEffect,
    useRef,
    useCallback,
} from 'react';
import { storeWorkflowStep } from '../ftue-workflow/utils';
import { useSaveAiRecommendationData } from './hooks/use-save-ai-recommendations';
import {
    CONCIERGE_WORKFLOW_IDS,
    ConciergeState,
    SelectionResponseObj,
} from './types';

export const ConciergeContext = createContext<ConciergeState | undefined>(
    undefined
);

/*
 * The guide menu choice has to outlive a reload: the workflow step is persisted, so refreshing
 * past the menu restores the step but would otherwise drop the goal/topic branch back to goals,
 * silently saving topic selections as goals. Stored in sessionStorage rather than localStorage
 * because the choice is check-in data and must not survive the session (HIPAA).
 */
function hydrateSelectedCheckinType(): CheckinTypeKey {
    if (isSSR()) return CheckinTypeKey.Goal;
    const stored = sessionStorage.getItem(FtueStoredCheckinTypeName);
    return Object.values(CheckinTypeKey).includes(stored as CheckinTypeKey)
        ? (stored as CheckinTypeKey)
        : CheckinTypeKey.Goal;
}

function persistSelectedCheckinType(checkinType: CheckinTypeKey) {
    if (isSSR()) return;
    sessionStorage.setItem(FtueStoredCheckinTypeName, checkinType);
}

/* An unrecognised `source` param reports nothing rather than a made-up Source. */
function parseConciergeSource(
    source: string | string[] | undefined
): ConciergeSource | undefined {
    const value = Array.isArray(source) ? source[0] : source;
    return Object.values(ConciergeSource).find(
        (candidate) => candidate === value
    );
}

function parseGuidePageSource(
    source: string | string[] | undefined
): EnumGuidePageSource | undefined {
    const value = Array.isArray(source) ? source[0] : source;
    return Object.values(EnumGuidePageSource).find(
        (candidate) => candidate === value
    );
}

export function ConciergeProvider({ children }: { children: ReactNode }) {
    const hasRestartedWorkflow = useRef(false);
    /* Mirrors the ref above in state so `isResolvingStep` re-renders once the
     * restart attempt settles - a ref read can't invalidate a memo. */
    const [hasSettledRestart, setHasSettledRestart] = useState(false);
    const completedTypewriterTitles = useRef<Set<string>>(new Set());
    const {
        canCreateConcierge,
        canDeleteConcierge,
        isPermissionsSettled,
        isPermissionsLive,
    } = useConciergePermissions();
    const isConciergeLoading = !isPermissionsSettled;
    const { pathname, push, query } = useRouter();
    const isNewPlan = Boolean(query[isNewPlanQueryString]);
    const conciergeSource = parseConciergeSource(
        query[conciergeSourceQueryString]
    );
    const guidePageSource = parseGuidePageSource(
        query[guidePageSourceQueryString]
    );
    const {
        restartWorkflow,
        currentStep,
        setCurrentStep,
        isResolvingStep: isWorkflowResolvingStep,
    } = useWorkflow();
    const {
        isEnrolled: isConciergeGAFlagEnrolled,
        isLoading: isConciergeGAFlagLoading,
    } = useAmplitudeExperiment({
        experimentName: AmplitudeFeatureFlagNames.CONCIERGE_GA_ENABLED,
    });
    const isConciergeGAFlagOn =
        !isConciergeGAFlagLoading && !!isConciergeGAFlagEnrolled;
    const isConciergeGAEnabled = canCreateConcierge && isConciergeGAFlagOn;
    /* Both flows read as disabled until the circle's permissions and the GA
     * flag settle, so the hold below has to cover that gap rather than let a
     * consumer act on a not-yet-known enablement. */
    const isConciergeEnablementLoading =
        isConciergeLoading || isConciergeGAFlagLoading;

    const [menuSelectedCheckinType, setMenuSelectedCheckinType] =
        useState<CheckinTypeKey>(hydrateSelectedCheckinType);
    const setSelectedCheckinType = useCallback(
        (checkinType: CheckinTypeKey) => {
            persistSelectedCheckinType(checkinType);
            setMenuSelectedCheckinType(checkinType);
        },
        []
    );
    /*
     * The guide menu (goals vs topics) is only shown to concierge GA users.
     * For everyone else the menu is skipped, so the selection flow always
     * defaults to goals data regardless of any previously stored menu choice.
     */
    const selectedCheckinType = isConciergeGAEnabled
        ? menuSelectedCheckinType
        : CheckinTypeKey.Goal;
    const [selectionResponseData, setSelectionResponseData] = useState<
        SelectionResponseObj | undefined
    >();
    const [usercryptData, setUsercryptData] = useState<
        UsercryptCheckinEntrySummary | undefined
    >();
    const [conversationData, setConversationData] = useState<
        ConversationResponse | undefined
    >(undefined);
    const [refinementStepVersion, setRefinementStepVersion] =
        useState<number>(1);
    const [isUpdatingSelection, setIsUpdatingSelection] =
        useState<boolean>(false);

    /* Initialize mutation hooks */
    const {
        saveAiConversationDataAsync,
        isPending: isSaveConversationPending,
    } = useSaveAiConversationData();
    const {
        saveAiRecommendationDataAsync,
        isPending: isSaveRecommendationPending,
        hasPlanGenerationFailed,
        clearPlanGenerationFailure,
        generatedCollectionId,
        clearGeneratedCollectionId,
    } = useSaveAiRecommendationData({
        isConciergeGAFlagEnrolled: isConciergeGAFlagOn,
        isNewPlan,
    });
    const {
        conversationId: fetchedConversationId,
        isLoading: isConversationIdLoading,
    } = useLatestConversationId({
        conversationType: ConversationConversationTypeEnum.Onboarding,
    });

    const {
        workflows = [],
        isFetched: isFetchedWorkflows,
        isPending: isPendingWorkflowInstances,
    } = useAllWorkflowInstances({
        workflowStates: [
            WorkflowWorkflowState.InProgress,
            WorkflowWorkflowState.Completed,
        ],
    });

    const unfinishedConciergeWorkflows = useMemo(
        () =>
            workflows.filter(
                (workflow) =>
                    CONCIERGE_WORKFLOW_IDS.includes(
                        workflow?.workflow.defnId
                    ) &&
                    workflow?.workflow.state ===
                        WorkflowWorkflowState.InProgress
            ),
        [workflows]
    );
    const completedConciergeWorkflows = useMemo(
        () =>
            workflows.filter(
                (workflow) =>
                    CONCIERGE_WORKFLOW_IDS.includes(
                        workflow?.workflow.defnId
                    ) &&
                    workflow?.workflow.state === WorkflowWorkflowState.Completed
            ),
        [workflows]
    );

    const lastKnownStepValue = useMemo(() => {
        return currentStep || sessionStorage.getItem(FtueStoredStepName);
    }, [currentStep]);

    const isPending = useMemo(
        () =>
            isSaveConversationPending ||
            isSaveRecommendationPending ||
            isPendingWorkflowInstances,
        [
            isSaveConversationPending,
            isSaveRecommendationPending,
            isPendingWorkflowInstances,
        ]
    );

    /*
     * Holds consumers off while a step is still on its way - they render and run
     * their own effects before this provider's, so without this they can end the
     * workflow mid-fetch. Uses `hasSettledRestart` state rather than
     * `hasRestartedWorkflow.current`, which flips when the restart starts and
     * would drop the hold mid-restart (and latch `true` on a restart that
     * resolves no step). The enablement term must match the restart effect's
     * gate, since only that effect flips `hasSettledRestart`.
     */
    const isResolvingStep = useMemo(
        () =>
            isWorkflowResolvingStep ||
            isPending ||
            isConciergeEnablementLoading ||
            (canCreateConcierge &&
                !hasSettledRestart &&
                !!unfinishedConciergeWorkflows.length &&
                !lastKnownStepValue),
        [
            isWorkflowResolvingStep,
            isPending,
            isConciergeEnablementLoading,
            canCreateConcierge,
            hasSettledRestart,
            unfinishedConciergeWorkflows,
            lastKnownStepValue,
        ]
    );

    useEffect(() => {
        if (
            !isFetchedWorkflows ||
            isPending ||
            /* Only the users whose onboarding is concierge-driven, not everyone who may view concierge */
            !canCreateConcierge ||
            hasRestartedWorkflow.current ||
            // Dont go through restart flow if the user has no in progress workflows and already completed one of the same type before
            (completedConciergeWorkflows.length > 0 &&
                !unfinishedConciergeWorkflows.length)
        )
            return;

        const isRetakingSurveyStored =
            sessionStorage.getItem(STORED_RETAKING_SURVEY_KEY) === 'true';
        const storedStep = sessionStorage.getItem(FtueStoredStepName);

        /* True when the user is hard reloading the page when on the survey step after completing it once before */
        const isRefreshingSurveyRetry =
            !currentStep &&
            storedStep === FtueStepDefinitionIds.MENTAL_HEALTH_SCREENING &&
            isRetakingSurveyStored &&
            pathname === routes.concierge({});

        if (
            !!unfinishedConciergeWorkflows.length &&
            (!lastKnownStepValue || isRefreshingSurveyRetry)
        ) {
            /* The instance's own id, not the flag-derived one: a GA user's
             * workflow has to be restarted as the GA workflow, and a legacy
             * instance left behind by a flag flip as the legacy one. */
            const workflowId = unfinishedConciergeWorkflows[0].workflow.defnId;
            const callRestart = async () => {
                hasRestartedWorkflow.current = true;
                try {
                    await restartWorkflow(workflowId);
                    clearFtueSessionStorage();
                } catch (error) {
                    logger.error(
                        'Error restarting concierge workflow in provider',
                        {
                            workflowId,
                        },
                        error as Error
                    );
                    /*
                     * If restart call fails for any reason, set the step to END_WORKFLOW and redirect back to home screen to avoid blocking the user
                     * Since workflow instance itself is still in progress, the next time they start a session, it will try the restartWorkflow redirect again
                     */
                    setCurrentStep(FtueStepDefinitionIds.END_WORKFLOW);
                    storeWorkflowStep({
                        stepValue: FtueStepDefinitionIds.END_WORKFLOW,
                        data: undefined,
                    });
                    push(routes.dashboard).catch((err) =>
                        logger.error(
                            'Failed to navigate to dashboard',
                            {},
                            err as Error
                        )
                    );
                } finally {
                    setHasSettledRestart(true);
                }
            };
            callRestart().catch((err) =>
                logger.error(
                    'Unexpected failure in concierge callRestart',
                    {},
                    err as Error
                )
            );
        }
    }, [
        isFetchedWorkflows,
        restartWorkflow,
        setCurrentStep,
        currentStep,
        pathname,
        hasRestartedWorkflow,
        canCreateConcierge,
        lastKnownStepValue,
        isPending,
        unfinishedConciergeWorkflows,
        completedConciergeWorkflows,
        push,
    ]);

    const handleConversationSave = async (
        responseData: ConversationResponse
    ) => {
        setConversationData(responseData);
        await saveAiRecommendation(responseData.conversationId);
    };

    /* Concierge AI conversation fn for echo back */
    async function saveAiConversation(
        conversationRequest: ConversationRequest,
        include?: ConversationInclude
    ) {
        await saveAiConversationDataAsync({
            conversationRequest,
            conversationType: ConversationConversationTypeEnum.Onboarding,
            onSave: handleConversationSave,
            ...(include && { include }),
        });
    }

    async function saveAiRecommendation(conversationId: string) {
        if (!conversationId) {
            return;
        }

        const outcome = await saveAiRecommendationDataAsync({
            conversationId,
        });

        return outcome.path === 'plan' ? outcome.collectionId : undefined;
    }

    function retrieveRefinementMultipleChoiceOptions({
        parentChoiceId,
    }: {
        parentChoiceId: string;
    }) {
        const category = refinementStepVersion === 1 ? 'secondary' : 'tertiary';
        return usercryptData?.meta?.requirements?.[
            category
        ]?.multiselect?.choices.filter(
            (choice) => choice.parentChoiceId === parentChoiceId
        );
    }

    function retrieveRefinementHeadingData({ choiceId }: { choiceId: string }) {
        const currentObj =
            refinementStepVersion === 1 ? 'primary' : 'secondary';
        const selectedChoiceObj = usercryptData?.meta?.requirements?.[
            currentObj
        ]?.multiselect?.choices.filter((choice) => choice.id === choiceId);
        return selectedChoiceObj?.[0]?.description;
    }

    const markTypewriterTitleComplete = useCallback((title: string) => {
        completedTypewriterTitles.current.add(title);
    }, []);

    const isTypewriterTitleComplete = useCallback((title: string) => {
        return completedTypewriterTitles.current.has(title);
    }, []);

    const clearTypewriterTitleCache = useCallback(() => {
        completedTypewriterTitles.current.clear();
    }, []);

    // Prefer conversationData from state, fallback to fetched
    const conversationId =
        conversationData?.conversationId ?? fetchedConversationId;

    return (
        <ConciergeContext.Provider
            value={{
                isConciergeLoading,
                isConciergePermissionsLive: isPermissionsLive,
                isResolvingStep,
                isConciergeGAEnabled,
                isConciergeGALoading: isConciergeEnablementLoading,
                canCreateConcierge,
                canDeleteConcierge,
                isNewPlan,
                conciergeSource,
                guidePageSource,
                selectedCheckinType,
                setSelectedCheckinType,
                usercryptData,
                setUsercryptData,
                selectionResponseData,
                setSelectionResponseData,
                conversationData,
                setConversationData,
                refinementStepVersion,
                setRefinementStepVersion,
                retrieveRefinementMultipleChoiceOptions,
                retrieveRefinementHeadingData,
                saveAiConversation,
                isSaveConversationPending,
                saveAiRecommendation,
                isSaveRecommendationPending,
                hasPlanGenerationFailed,
                clearPlanGenerationFailure,
                generatedCollectionId,
                clearGeneratedCollectionId,
                isUpdatingSelection,
                setIsUpdatingSelection,
                markTypewriterTitleComplete,
                isTypewriterTitleComplete,
                clearTypewriterTitleCache,
                conversationId,
                isConversationIdLoading,
            }}
        >
            {children}
        </ConciergeContext.Provider>
    );
}
